import React from 'react'
import { Button, ErrorForm, HeaderPage, InputForm, SwitchDefault } from '@components/index'
import { useNavigate, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { useYupValidation } from '@hooks/index'
import userRealtorSchema from '@yupSchemas/userManagement/userRealtorSchema'
import { ICreateUpdateUser } from '@interfaces/userManagement/ICreateUpdateUser'
import { toast } from 'react-toastify'
import { errorHandler } from '@services/index'
import UserManagementService from '@services/requests/userManagementService'
import { SpinnerLoader } from '@assets/icons'
import { USER_STATUS } from '@interfaces/userManagement/IUserManagement'
import { FormControlLabel } from '@mui/material'
import { phoneMask, numericalMask, unMaskAll } from '@utils/index'

export default function RealtorsForm(): React.ReactElement {
    const [isLoading, setIsLoading] = React.useState(false)
    const navigate = useNavigate()
    const params = useParams()
    const id = params?.id

    interface IFormCreateUpdateUserAdmin extends Omit<ICreateUpdateUser, 'status'> {
        status?: boolean
    }

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<IFormCreateUpdateUserAdmin>({
        resolver: useYupValidation(userRealtorSchema),
        mode: 'all',
        defaultValues: {
            name: '',
            email: '',
            creci: '',
            estateAgency: '',
            phone: '',
            status: false,
        },

    })

    async function submit(values: IFormCreateUpdateUserAdmin): Promise<void> {
        setIsLoading(true)

        try {
            let message = 'Erro ao Salvar Usuário!'
            if (id) {
                const response = await UserManagementService.editUser(Number(params.id), {
                    ...values,
                    phone: unMaskAll(values.phone || ''),
                    status: values.status ? USER_STATUS.ACTIVE : USER_STATUS.INACTIVE
                })

                message = response.message
            }

            toast(message, { type: 'success' })
            navigate('/corretores')
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    async function getUserData(id: number): Promise<void> {
        setIsLoading(true)
        try {
            const response = await UserManagementService.getUser(id)

            reset({
                name: response.name,
                email: response.email,
                creci: response.creci,
                estateAgency: response.estateAgency,
                phone: phoneMask(response.phone || ''),
                status: response.status === USER_STATUS.ACTIVE,
            })
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    React.useEffect(() => {
        if (params?.id) {
            getUserData(Number(params.id))
        }
    }, [])

    function applyMaskHandler(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        onChange: (event: string) => void,
        maskFunction: (event: string) => string
    ): void {
        const newValue = maskFunction(event.target.value)
        onChange(newValue)
    }

    return (
        <div className="card">
            <div className="create-update-users">
                <div className='box-header'>
                    <HeaderPage title={params?.id ? 'Editar Corretor' : 'Novo Corretor'} />
                </div>

                <form onSubmit={handleSubmit(submit)}>
                    <div className='box-header' />

                    <div className='box-inputs'>
                        <div className="box-controller">
                            <Controller
                                name="name"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            label="Nome"
                                            placeholder='Digite o nome do corretor'
                                            name="name"
                                            value={value}
                                            inputProps={{ maxLength: 50 }}
                                            onChange={onChange}
                                            size='small'

                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.name?.message as string} />
                        </div>

                        <div className="box-controller">
                            <Controller
                                name="email"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            label="E-mail"
                                            placeholder='example@email.com'
                                            name="email"
                                            value={value}
                                            onChange={onChange}
                                            size='small'
                                            inputProps={{ maxLength: 80 }}
                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.email?.message as string} />
                        </div>

                        <div className="box-controller">
                            <Controller
                                name="creci"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            label="CRECI"
                                            placeholder='897951'
                                            name="creci"
                                            value={value}
                                            onChange={evt =>
                                                applyMaskHandler(evt, onChange, numericalMask)
                                            }
                                            size='small'
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.creci?.message as string} />
                        </div>

                        <div className="box-controller">
                            <Controller
                                name="estateAgency"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            label="Imobiliária"
                                            placeholder='Imobiliária ...'
                                            name="estateAgency"
                                            value={value}
                                            onChange={onChange}
                                            size='small'
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.estateAgency?.message as string} />
                        </div>

                        <div className="box-controller">
                            <Controller
                                name="phone"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            label="Celular"
                                            placeholder='(55) 99999-9999'
                                            name="phone"
                                            value={value}
                                            onChange={evt =>
                                                applyMaskHandler(evt, onChange, phoneMask)
                                            }
                                            size='small'
                                            inputProps={{ maxLength: 15 }}
                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.phone?.message as string} />
                        </div>
                    </div>
                    {id &&
                        <div className='box-controller'>
                            <Controller
                                name='status'
                                control={control}
                                defaultValue={true}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <FormControlLabel style={{ margin: 0 }} labelPlacement='top' value='top' control={<SwitchDefault value={value} onChange={onChange} checked={value} />} label='Status' />
                                            <label style={{ fontSize: '0.8rem', marginTop: '1.5rem', fontFamily: 'Open sans' }}>{value ? 'Ativo' : 'Inativo'}</label>
                                        </div>
                                    )
                                }
                                }
                            />
                        </div>
                    }
                    <div className="box-buttons">
                        <Button type="button" secondary placeholder={'Cancelar'} onClick={() => navigate('/corretores')} />
                        <Button type="submit" isLoading={isLoading} placeholder={'Salvar'} />
                    </div>
                    {isLoading ? (
                        <div className="loader-page">
                            <SpinnerLoader />
                        </div>
                    ) : (
                        <></>
                    )}
                </form>
            </div>
        </div>
    )
}